import HttpClient from "./http.client";
import { apiConfig } from "@/config/api.config";

// Initialize HTTP client for Account service
const accountClient = new HttpClient(apiConfig.account.baseUrl);

// Authentication interfaces
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber?: string;
}

export interface RegisterResponse {
  otpId: string;
}

export interface VerifyOtpData {
  otpId: string;
  otp: string;
}

export interface ForgetPasswordResponse {
  otpId: string;
}

export interface ValidateResetPasswordOtpData {
  otpId: string;
  otp: string;
}

export interface ValidateResetPasswordOtpResponse {
  userId: string;
  token: string;
}

export interface ResetPasswordData {
  userId: string;
  token: string;
  password: string;
  confirmPassword: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface AddReviewData {
  hotelId: string;
  rating: number;
  comment: string;
}

export interface HotelReviewItem {
  id: string;
  name: string;
  rating: number;
  comment: string;
  dateCreated: string;
}

export interface HotelReviewsPage {
  items: HotelReviewItem[];
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
  };
}

// Envelope shape used across the Roomsvile APIs (see FeaturedHotelsResponse,
// HotelDetailsResponse, StatesResponse, etc. in @/types/api.types)
export interface ApiEnvelope<T> {
  data: T;
  status: string;
  statusCode: string;
  message: string;
}

// The "id" here is the same hotel GUID used everywhere else (HotelDetails.id);
// "hotelId" is the human-readable property code (HotelDetails.hotelId), not a GUID.
export interface FavouriteHotelItem {
  id: string;
  hotelId: string;
  name: string;
  address: string;
  email: string;
  phoneNumber: string;
  rating: number;
  description: string;
  hotelImages: { id: string; imageUrl: string }[];
}

export interface FavouritesPage {
  items: FavouriteHotelItem[];
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
  };
}

// Account Service API calls
export const accountService = {
  // Authentication
  login: async (credentials: LoginCredentials) => {
    return await accountClient.post<ApiEnvelope<AuthResponse>>(
      "Users/v1/login",
      credentials
    );
  },

  register: async (data: RegisterData) => {
    return await accountClient.post<ApiEnvelope<RegisterResponse>>(
      "Users/v1/create-user",
      data
    );
  },

  verifyOtp: async (data: VerifyOtpData) => {
    return await accountClient.post<ApiEnvelope<null>>(
      "Users/v1/verify-account",
      data
    );
  },

  resendOtp: async (otpId: string) => {
    return await accountClient.post<ApiEnvelope<RegisterResponse>>(
      "Users/v1/resend-otp",
      { otpId }
    );
  },

  logout: async () => {
    return await accountClient.post("Users/v1/logout");
  },

  refreshToken: async () => {
    return await accountClient.post<ApiEnvelope<AuthResponse>>(
      "Users/v1/refresh-token"
    );
  },

  // User Profile
  getProfile: async () => {
    return await accountClient.get<ApiEnvelope<User>>("Users/v1/get-profile");
  },

  updateProfile: async (data: Partial<User>) => {
    return await accountClient.put<ApiEnvelope<User>>(
      "Users/v1/update-profile",
      data
    );
  },

  changePassword: async (
    oldPassword: string,
    password: string,
    confirmPassword: string
  ) => {
    return await accountClient.post<ApiEnvelope<null>>(
      "Users/v1/change-password",
      {
        oldPassword,
        password,
        confirmPassword,
      }
    );
  },

  deleteAccount: async (password: string) => {
    return await accountClient.delete<ApiEnvelope<null>>(
      "Users/v1/delete-profile",
      { data: { password } }
    );
  },

  // Password Reset
  forgotPassword: async (email: string) => {
    return await accountClient.get<ApiEnvelope<ForgetPasswordResponse>>(
      `Users/v1/forget-password/${encodeURIComponent(email)}`
    );
  },

  validateResetPasswordOtp: async (data: ValidateResetPasswordOtpData) => {
    return await accountClient.post<
      ApiEnvelope<ValidateResetPasswordOtpResponse>
    >("Users/v1/verify-forget-password-otp", data);
  },

  resetPassword: async (data: ResetPasswordData) => {
    return await accountClient.post<ApiEnvelope<null>>(
      "Users/v1/reset-password",
      data
    );
  },

  // Email verification
  verifyEmail: async (token: string) => {
    return await accountClient.post("auth/verify-email", { token });
  },

  resendVerificationEmail: async () => {
    return await accountClient.post("auth/resend-verification");
  },

  // Hotels
  getFeaturedHotels: async () => {
    return await accountClient.get("Hotels/v1/get-featured-hotels");
  },

  getNearbyHotels: async (params: {
    latitude?: number;
    longitude?: number;
    searchTerm?: string;
    stateId?: string;
    cityId?: string;
    propertyType?: string;
    pageNumber?: number;
    pageSize?: number;
  }) => {
    const queryParams = new URLSearchParams();

    if (params.latitude !== undefined) {
      queryParams.append("Latitude", params.latitude.toString());
    }
    if (params.longitude !== undefined) {
      queryParams.append("Longitude", params.longitude.toString());
    }
    if (params.searchTerm) {
      queryParams.append("SearchTerm", params.searchTerm);
    }
    if (params.stateId) {
      queryParams.append("StateId", params.stateId);
    }
    if (params.cityId) {
      queryParams.append("CityId", params.cityId);
    }
    if (params.propertyType) {
      queryParams.append("PropertyType", params.propertyType);
    }
    queryParams.append("PageNumber", (params.pageNumber || 1).toString());
    queryParams.append("PageSize", (params.pageSize || 10).toString());

    return await accountClient.get(
      `Hotels/v1/get-hotels-near-you?${queryParams}`
    );
  },

  // Location Services
  getStates: async (countryId: string) => {
    return await accountClient.get(`State/v1/get-states/${countryId}`);
  },

  getCitiesByState: async (stateId: string) => {
    return await accountClient.get(`City/v1/get-cities-by-state/${stateId}`);
  },

  // Hotel Details
  getHotelDetails: async (hotelId: string) => {
    return await accountClient.get(`Hotels/v1/get-hotel/${hotelId}`);
  },

  // Reviews
  addReview: async (data: AddReviewData) => {
    return await accountClient.post<ApiEnvelope<null>>(
      "Review/v1/add-review",
      data
    );
  },

  // Public endpoint - no auth required, so logged-out visitors can read reviews.
  getHotelReviews: async (hotelId: string, pageNumber = 1, pageSize = 10) => {
    const queryParams = new URLSearchParams();
    queryParams.append("HotelId", hotelId);
    queryParams.append("PageNumber", pageNumber.toString());
    queryParams.append("PageSize", pageSize.toString());

    return await accountClient.get<ApiEnvelope<HotelReviewsPage>>(
      `Review/v1/get-hotel-review?${queryParams}`
    );
  },

  // Favourites
  addFavourite: async (hotelId: string) => {
    return await accountClient.post<ApiEnvelope<null>>(
      "Favourites/v1/add-favourite",
      { hotelId }
    );
  },

  removeFavourite: async (hotelId: string) => {
    return await accountClient.delete<ApiEnvelope<null>>(
      `Favourites/v1/remove-favourite?hotelId=${encodeURIComponent(hotelId)}`
    );
  },

  getFavourites: async (pageNumber = 1, pageSize = 10) => {
    const queryParams = new URLSearchParams();
    queryParams.append("PageNumber", pageNumber.toString());
    queryParams.append("PageSize", pageSize.toString());

    return await accountClient.get<ApiEnvelope<FavouritesPage>>(
      `Favourites/v1/get-favourites?${queryParams}`
    );
  },
};

export default accountService;
